import { makeStyles, Theme } from "@material-ui/core/styles";
import { BaseCSSProperties } from "@material-ui/core/styles/withStyles";
import PersonCheckIcon from "@material-ui/icons/HowToReg";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import PersonAddDisabledIcon from "@material-ui/icons/PersonAddDisabled";
import SendIcon from "@material-ui/icons/Send";
import { useRef, useState } from "react";
import { changeAppState } from "../../../../../../common/appState";
import { useRelatedUsers } from "../../../../../../common/hooks/useRelatedUsers";
import { useUser } from "../../../../../../common/hooks/useUser";
import { UnfriendIcon } from "../../../../../../icon/UnfriendIcon";
import ShurikenProgress from "../../../../Animations/ShurikenProgress";
import { DarkButton } from "../../../../Button/DarkButton";
import { PrimaryButton } from "../../../../Button/PrimaryButton";
import { DarkPopoverMenu } from "../../../../Popover/DarkPopoverMenu";
import { User, UserBase } from "../../../types";
import {
    fetchAddFriend,
    fetchApprove,
    fetchCancelRequest,
    fetchUnfriend,
} from "./functions";

export function FriendButtonArea({
    style,
    targetUser,
    isSmartPhone,
    hideUnapprovedRequestFromThem,
}: {
    style?: BaseCSSProperties;
    targetUser: UserBase;
    isSmartPhone?: boolean;
    hideUnapprovedRequestFromThem?: boolean;
}) {
    const c = useStyles({ style, isSmartPhone });
    const { relatedUsers } = useRelatedUsers();
    const relation = relatedUsers.find(u => u.userId === targetUser.userId);
    const isFriend = relation?.isFriend ?? false;
    const requestFromMe = relation?.requestFromMe;
    const hasUnapprovedRequestFromThem =
        !!relation?.requestFromThem && !relation.requestFromThem.approved;
    const cancelable = !isFriend && !!requestFromMe && !requestFromMe.canceled;

    const { user } = useUser();

    return (
        <>
            {!hideUnapprovedRequestFromThem && hasUnapprovedRequestFromThem && (
                <span className={c.friendRequestMessage}>
                    Sent you a friend request
                </span>
            )}
            <div className={c.container}>
                <div className={c.friendButtonContainer}>
                    <FriendButton
                        isFriend={isFriend}
                        hasUnapprovedRequestFromThem={
                            hasUnapprovedRequestFromThem
                        }
                        cancelable={cancelable}
                        targetUser={targetUser}
                        user={user}
                    />
                </div>
                <div className={c.messageButtonContainer}>
                    <DarkButton
                        className={c.sendButton}
                        onClick={() => {
                            if (user) {
                                changeAppState("messagePanelState", {
                                    targetUserId: targetUser.userId,
                                });
                                return;
                            }
                            changeAppState("signInPanelState", {
                                type: "signUp",
                            });
                        }}
                    >
                        <SendIcon />
                    </DarkButton>
                </div>
            </div>
        </>
    );
}
const useStyles = makeStyles<
    Theme,
    { style?: BaseCSSProperties; isSmartPhone?: boolean }
>(theme => ({
    container: ({ style }) => ({ display: "flex", ...style }),
    friendButtonContainer: { width: "100%", marginRight: 5 },
    messageButtonContainer: {
        marginLeft: 5,
    },
    sendButton: {
        height: "100%",
        maxHeight: 40,
        maxWidth: 45,
        minWidth: 45,
        backgroundColor: theme.palette.grey[800],
    },
    friendRequestMessage: ({ isSmartPhone }) => ({
        color: theme.palette.grey[700],
        position: "relative",
        top: isSmartPhone ? -2 : 10,
    }),
}));

function FriendButton({
    isFriend,
    hasUnapprovedRequestFromThem,
    cancelable,
    targetUser,
    user,
}: {
    isFriend: boolean;
    hasUnapprovedRequestFromThem: boolean;
    cancelable: boolean;
    targetUser: UserBase;
    user?: User;
}) {
    const [submitting, setSubmitting] = useState(false);

    if (!user) {
        return (
            <PrimaryButton
                fullWidth
                startIcon={<PersonAddIcon />}
                onClick={() => {
                    changeAppState("signInPanelState", { type: "signUp" });
                }}
            >
                Add Friend
            </PrimaryButton>
        );
    }
    if (isFriend) {
        return (
            <AlreadyFriendButton
                unfriend={async () => {
                    setSubmitting(true);
                    await fetchUnfriend(user.userId, targetUser.userId);
                    setSubmitting(false);
                }}
                submitting={submitting}
            />
        );
    }
    if (hasUnapprovedRequestFromThem) {
        return (
            <PrimaryButton
                fullWidth
                startIcon={submitting ? undefined : <PersonAddIcon />}
                onClick={async () => {
                    setSubmitting(true);
                    await fetchApprove(user.userId, targetUser.userId);
                    setSubmitting(false);
                }}
                disabled={submitting}
                style={{ height: 40 }}
            >
                {submitting ? <ShurikenProgress size={15} /> : "Confirm"}
            </PrimaryButton>
        );
    }
    if (cancelable) {
        return (
            <DarkButton
                fullWidth
                startIcon={submitting ? undefined : <PersonAddDisabledIcon />}
                onClick={async () => {
                    setSubmitting(true);
                    await fetchCancelRequest(user.userId, targetUser.userId);
                    setSubmitting(false);
                }}
                disabled={submitting}
                style={{ height: 40 }}
            >
                {submitting ? <ShurikenProgress size={15} /> : "Cancel"}
            </DarkButton>
        );
    }
    return (
        <PrimaryButton
            fullWidth
            startIcon={submitting ? undefined : <PersonAddIcon />}
            onClick={async () => {
                setSubmitting(true);
                await fetchAddFriend(user.userId, targetUser.userId);
                setSubmitting(false);
            }}
            disabled={submitting}
            style={{ height: 40 }}
        >
            {submitting ? <ShurikenProgress size={15} /> : "Add Friend"}
        </PrimaryButton>
    );
}

function AlreadyFriendButton({
    unfriend,
    submitting,
}: {
    unfriend: () => void;
    submitting: boolean;
}) {
    const [isMenuOpen, setMenuOpen] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    return (
        <div ref={containerRef}>
            <DarkButton
                ref={buttonRef}
                fullWidth
                startIcon={submitting ? undefined : <PersonCheckIcon />}
                onClick={() => {
                    setMenuOpen(true);
                }}
                style={{ opacity: isMenuOpen ? 0.7 : 1, height: 40 }}
                disabled={submitting}
            >
                {submitting ? <ShurikenProgress size={15} /> : "Friends"}
            </DarkButton>
            <DarkPopoverMenu
                anchorEl={buttonRef.current}
                containerEl={containerRef.current}
                open={isMenuOpen}
                onClose={() => {
                    setMenuOpen(false);
                }}
                items={[
                    {
                        label: "Unfriend",
                        icon: <UnfriendIcon />,
                        onClick: unfriend,
                    },
                ]}
                disabled={submitting}
            />
        </div>
    );
}
